/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Form, Pagination } from 'react-bootstrap'; // Importei Row e Col
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa'; // Importei FaEdit e FaTrash

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';

const formatTelefone = (telefone) => {
  if (!telefone) return '';
  const ddd = telefone.slice(0, 2);
  const prefix = telefone.slice(2, 6);
  const suffix = telefone.slice(6);
  return `(${ddd}) ${prefix}-${suffix}`;
};

const formatCelular = (celular) => {
  if (!celular) return '';
  const ddd = celular.slice(0, 2);
  const prefix = celular.slice(2, 7);
  const suffix = celular.slice(7);
  return `(${ddd}) ${prefix}-${suffix}`;
};

export default function Requesters() {
  const routeName = '/requesters';
  const dataName = 'Solicitante';
  const titleName = 'Solicitantes';
  const navigate = useNavigate();
  const notification = useNotification();

  const [apiErrors, setApiErrors] = useState({});
  const [requesters, setRequesters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const tableColumns = useMemo(
    () => [
      { Header: 'Nome', accessor: 'name' },
      { Header: 'E-mail', accessor: 'email' },
      { Header: 'Empresa', accessor: 'companyName' },
      { Header: 'Telefone', accessor: 'telefone', noSearch: true },
      { Header: 'Celular', accessor: 'celular', noSearch: true },
      { Header: 'Ramal', accessor: 'ramal', noSearch: true },
      {
        Header: 'Ações',
        accessor: 'actions',
        noSearch: true,
        disableSortBy: true,
      },
    ],
    []
  );

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(routeName, {
        params: {
          q: debouncedSearchText,
          page: currentPage,
          limit,
        },
      });
      setRequesters(response.data.requesters);
      setTotalCount(response.data.totalCount);
      setTotalPages(Math.ceil(response.data.totalCount / limit));
    } catch (err) {
      setApiErrors(err);
      setRequesters([]);
    } finally {
      setIsLoading(false);
    }
  }, [routeName, debouncedSearchText, currentPage, limit, notification]);

  useEffect(() => {
    getData();
  }, [getData]);

  const confirmDelete = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        await axios.delete(`${routeName}/${id}`);
        notification(`${dataName} removido com sucesso`, 'success');
        getData();
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    },
    [notification, getData]
  );

  const tableData = useMemo(() => {
    return requesters.map((item) => {
      const companyName = item.Customer
        ? item.Customer.razaoSocial || item.Customer.nomeEmpresa
        : 'N/A';

      return {
        name: item.name,
        email: item.email,
        companyName,
        telefone: formatTelefone(item.telefone),
        celular: formatCelular(item.celular),
        ramal: item.ramal,
        // CORREÇÃO: Botões de Editar e Deletar diretos
        actions: (
          <div className="d-flex justify-content-end gap-1">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`${routeName}/${item.id}/edit`)}
            >
              <FaEdit /> Editar
            </Button>
            <ButtonModal
              onConfirm={() => confirmDelete(item.id)}
              message={`Confirma remoção do ${dataName} ${item.name}?`}
              buttonProps={{ variant: 'danger', size: 'sm' }}
              textModal={{
                title: `Remover ${dataName}`,
                textConfirm: 'Remover',
              }}
            >
              <FaTrash />
            </ButtonModal>
          </div>
        ),
      };
    });
  }, [requesters, confirmDelete, navigate, dataName, routeName]);

  const renderPaginationItems = () => {
    const pages = [];
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (startPage > 1) {
      pages.push(
        <Pagination.First key="first" onClick={() => setCurrentPage(1)} />
      );
      pages.push(
        <Pagination.Prev
          key="prev-ellipses"
          onClick={() => setCurrentPage(currentPage - 1)}
        />
      );
      pages.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    } else {
      pages.push(
        <Pagination.Prev
          key="prev"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        />
      );
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      pages.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      pages.push(
        <Pagination.Next
          key="next-ellipses"
          onClick={() => setCurrentPage(currentPage + 1)}
        />
      );
      pages.push(
        <Pagination.Last
          key="last"
          onClick={() => setCurrentPage(totalPages)}
        />
      );
    } else {
      pages.push(
        <Pagination.Next
          key="next"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
      );
    }

    return pages;
  };

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title="Solicitantes" />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1 className="page-heading">{titleName}</h1>
        <div>
          <Link to={`${routeName}/new`}>
            <Button variant="primary" className="text-uppercase">
              <FaPlus /> Cadastrar
            </Button>
          </Link>
        </div>
      </div>

      <Form.Group
        controlId="search"
        className="mb-3"
        style={{ maxWidth: '200px' }}
      >
        <strong>Pesquisar</strong>
        <Form.Control
          type="search"
          placeholder="Digite aqui..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Form.Group>

      <section>
        <Card className="card-table mb-5">
          <ErrorProcessor error={apiErrors} />
          <DataTable
            items={tableData}
            columns={tableColumns}
            defaultPageSize={limit}
          />
        </Card>
      </section>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          Mostrando {requesters.length} de {totalCount} solicitantes
        </div>
        <Pagination>{renderPaginationItems()}</Pagination>
      </div>
    </Container>
  );
}
